import { useState, useEffect, useCallback } from 'react';
import { Bookmark } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBookmarksContext } from '@/contexts/BookmarksContext';

interface BookmarkToggleProps {
  docId: string;
  title: string;
  repoFullName: string;
  path: string;
  docType: string;
  className?: string;
}

export function BookmarkToggle({
  docId,
  title,
  repoFullName,
  path,
  docType,
  className,
}: BookmarkToggleProps) {
  const { bookmarkedSet, add, remove, computeHash } = useBookmarksContext();
  const [hash, setHash] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    computeHash(docId).then(setHash);
  }, [docId, computeHash]);

  const isBookmarked = hash ? bookmarkedSet.has(hash) : false;

  const handleToggle = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!hash || toggling) return;

    setToggling(true);
    try {
      if (isBookmarked) {
        await remove(hash);
      } else {
        await add({ docId, title, repoFullName, path, docType });
      }
    } catch {
      // Limit reached or other error — silently fail
    } finally {
      setToggling(false);
    }
  }, [hash, isBookmarked, toggling, docId, title, repoFullName, path, docType, add, remove]);

  if (!hash) return null;

  return (
    <button
      onClick={handleToggle}
      disabled={toggling}
      className={cn(
        'p-1.5 rounded transition-colors shrink-0',
        isBookmarked
          ? 'text-amber-500 hover:text-amber-600 dark:text-amber-400 dark:hover:text-amber-300'
          : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300',
        toggling && 'opacity-50',
        className,
      )}
      title={isBookmarked ? 'Remove bookmark' : 'Bookmark this document'}
      aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark this document'}
    >
      <Bookmark className="w-4 h-4" fill={isBookmarked ? 'currentColor' : 'none'} />
    </button>
  );
}
